import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ExternalLink, ListChecks } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  countDoneSteps,
  GETTING_STARTED_STEPS,
  useGettingStartedQuery,
} from "@/hooks/use-getting-started";
import { useServiceStore } from "@/stores/service-store";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { DownloadAppButton } from "@/components/download-app-button";
import { InstallSnippet } from "@/components/getting-started/install-snippet";
import type { GettingStartedStep } from "@/types/user";

// Steps can be completed away from the dashboard (on a phone, on the
// customer's site), so keep checking while the page is open.
const POLL_INTERVAL = 15000;

interface StepStatusProps {
  done: boolean;
}

function StepStatus({ done }: StepStatusProps) {
  return done ? (
    <svg viewBox="0 0 14 14" aria-hidden="true" className="size-3.5 shrink-0">
      <circle cx="7" cy="7" r="7" className="fill-emerald-500" />
      <path
        d="M4.25 7.25 6 9l3.75-4"
        fill="none"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ) : (
    <svg
      viewBox="0 0 14 14"
      aria-hidden="true"
      className="size-3.5 shrink-0 text-muted-foreground/40"
    >
      <circle
        cx="7"
        cy="7"
        r="6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeDasharray="2 3.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface StepContent {
  title: string;
  description: React.ReactNode;
  action: React.ReactNode;
}

function getStepContent(step: GettingStartedStep, serviceId: string): StepContent {
  switch (step) {
    case "script_installed":
      return {
        title: "Install the script",
        description: (
          <>
            Paste this snippet into your web app. Replace the{" "}
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
              user
            </code>{" "}
            value with a unique, non-sequential identifier for the signed-in
            user. For security, we recommend a hash (SHA-256 or MD5) of their
            ID combined with their registration date.
          </>
        ),
        action: (
          <>
            <InstallSnippet serviceId={serviceId} />
            <Button variant="outline" size="sm" asChild className="mt-4">
              <a href="https://docs.appio.so/" target="_blank" rel="noopener noreferrer">
                View full documentation
                <ExternalLink />
              </a>
            </Button>
          </>
        ),
      };
    case "device_added":
      return {
        title: "Add a device",
        description:
          "Install your app on your phone to experience Appio from your users' perspective.",
        action: <DownloadAppButton />,
      };
    case "notification_sent":
      return {
        title: "Send a notification",
        description: "Send a push notification to the devices that have your app.",
        action: (
          <Button size="sm" asChild>
            <Link to="/notifications/new">New Notification</Link>
          </Button>
        ),
      };
    case "widget_added":
      return {
        title: "Add a widget",
        description:
          "Add a widget to your phone's home screen: select the Appio app and choose the widget size.",
        action: (
          <Button variant="outline" size="sm" asChild>
            <Link to="/widgets">View widgets</Link>
          </Button>
        ),
      };
  }
}

// Account-level checklist; only the install snippet is per selected service.
// Reached from the card under the service picker, it has no menu item.
export function GettingStartedPage() {
  // Until useSelectedServiceSync (in AppLayout) picks a service, or sends a
  // user without one to onboarding, the checklist stays a skeleton.
  const { selectedServiceId } = useServiceStore();
  const {
    data: progress,
    isError,
    isFetching,
    refetch,
  } = useGettingStartedQuery({ pollInterval: POLL_INTERVAL });
  // undefined until a step is clicked; until then the first unfinished step
  // stays open, advancing by itself as steps get done.
  const [chosenStep, setChosenStep] = useState<GettingStartedStep | null>();

  const loadFailed = !progress && isError;

  const done = progress ? countDoneSteps(progress) : 0;
  const firstOpenStep = GETTING_STARTED_STEPS.find((step) => !progress?.[step]) ?? null;
  const openStep = chosenStep === undefined ? firstOpenStep : chosenStep;

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 pt-8 md:pt-12">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold tracking-[-0.01em]">
          Welcome to Appio
        </h1>
        <p className="text-sm text-muted-foreground">
          Use this checklist to get Appio running in your web app.
        </p>
      </div>

      <section className="overflow-hidden rounded-xl bg-card shadow-card">
        <div className="relative border-b p-6">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(var(--color-border)_1px,transparent_1px)] bg-size-[14px_14px] [mask-image:linear-gradient(to_bottom,black,transparent)]"
          />
          <div className="relative flex items-center gap-4">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-lg border bg-background">
              <ListChecks aria-hidden="true" className="size-6 text-muted-foreground" />
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <h2 className="text-lg font-semibold tracking-[-0.01em]">
                Get started with Appio
              </h2>
              <div className="flex items-center gap-3">
                {progress ? (
                  <p className="text-xs text-muted-foreground tabular-nums">
                    {done} of {GETTING_STARTED_STEPS.length} completed
                  </p>
                ) : (
                  !loadFailed && <Skeleton className="h-3 w-24" />
                )}
                <Progress
                  value={done}
                  max={GETTING_STARTED_STEPS.length}
                  label="Getting started progress"
                  className="w-32"
                />
              </div>
            </div>
          </div>
        </div>

        {loadFailed ? (
          <div className="flex items-center justify-between gap-4 px-6 py-5">
            <p role="alert" className="text-[13px] text-destructive">
              Failed to load your progress.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              Retry
            </Button>
          </div>
        ) : !progress || !selectedServiceId ? (
          <div className="flex flex-col px-6 py-2">
            {GETTING_STARTED_STEPS.map((step) => (
              <div key={step} className="flex min-h-12 items-center gap-4 py-3">
                <Skeleton className="size-3.5 rounded-full" />
                <Skeleton className="h-3.5 w-40" />
              </div>
            ))}
          </div>
        ) : (
          <ol className="px-6 py-2">
            {GETTING_STARTED_STEPS.map((step, index) => {
              const content = getStepContent(step, selectedServiceId);
              const isDone = !!progress?.[step];
              const isOpen = openStep === step;
              const panelId = `getting-started-${step}`;

              return (
                <li key={step}>
                  {index > 0 && <div className="ml-[30px] h-px bg-border" />}
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setChosenStep(isOpen ? null : step)}
                    className="group -mx-3 flex min-h-12 w-[calc(100%+1.5rem)] cursor-pointer items-center gap-4 rounded-lg px-3 py-3 text-left transition-colors hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <StepStatus done={isDone} />
                    <span
                      className={cn(
                        "flex-1 text-[13px] font-medium",
                        isDone && "text-muted-foreground"
                      )}
                    >
                      {content.title}
                      <span className="sr-only">{isDone ? ", done" : ", to do"}</span>
                    </span>
                    <ChevronRight
                      aria-hidden="true"
                      className={cn(
                        "size-4 shrink-0 text-muted-foreground/70 transition-transform duration-300 ease-out group-hover:text-foreground",
                        isOpen && "rotate-90"
                      )}
                    />
                  </button>
                  {isOpen && (
                    <div id={panelId} className="pb-5 pl-[30px]">
                      <p className="max-w-xl text-[13px] leading-5">
                        {content.description}
                      </p>
                      <div className="mt-4">{content.action}</div>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </div>
  );
}
